import { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Sidebar } from './sidebar';
import { TopBar } from './top-bar';
import { ImpersonationBanner } from './impersonation-banner';
import { useAuthStore } from '@/store/auth.store';
import { useImpersonationStore } from '@/store/impersonation.store';
import { useTheme } from '@/lib/theme';

// A crash inside one page shouldn't take down the sidebar/topbar with it —
// this scopes the boundary to just the routed content, and resets
// automatically on navigation so leaving the broken page recovers instead
// of staying stuck on the error screen.
function PageErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <h2 className="text-lg font-semibold" style={{ color: '#ef4444' }}>This page hit an error</h2>
      <pre className="text-xs max-w-xl whitespace-pre-wrap" style={{ color: 'var(--text-muted)' }}>{String(error)}</pre>
      <button
        onClick={resetError}
        className="px-5 py-2 rounded-lg text-sm font-semibold"
        style={{ background: 'linear-gradient(135deg,#22d3ee,#7c3aed)', color: '#fff' }}
      >
        Try again
      </button>
    </div>
  );
}

export function AppLayout() {
  const isRegularAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const isImpersonating = useImpersonationStore((s) => s.active);
  const isAuthenticated = isRegularAuthenticated || isImpersonating;
  const { theme } = useTheme();
  const location = useLocation();

  const [pinned, setPinned] = useState(() => {
    const saved = localStorage.getItem('sidebar-pinned');
    return saved === null ? true : saved === 'true';
  });
  const [hoverOpen, setHoverOpen] = useState(false);

  const sidebarOpen = pinned || hoverOpen;

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem('sidebar-pinned', String(next));
    if (!next) setHoverOpen(false);
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      {/* Dark-mode cyan grid overlay */}
      <div
        className="fixed inset-0 pointer-events-none dark:opacity-100 opacity-0 transition-opacity duration-500"
        style={{
          backgroundImage:
            'linear-gradient(rgba(34,211,238,0.022) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(34,211,238,0.022) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          zIndex: 0,
        }}
      />
      {/* E-ink dot matrix overlay */}
      {theme === 'e-ink' && (
        <div
          className="fixed inset-0 pointer-events-none transition-opacity duration-500"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            zIndex: 0,
            opacity: 1,
          }}
        />
      )}

      {/* Hover rail — thin strip on the left edge when sidebar is unpinned */}
      {!pinned && (
        <div
          className="fixed inset-y-0 left-0 z-50 w-2 cursor-pointer"
          onMouseEnter={() => setHoverOpen(true)}
          style={{
            background: theme === 'e-ink'
              ? 'rgba(0,0,0,0.08)'
              : 'linear-gradient(180deg, rgba(34,211,238,0.15), rgba(168,85,247,0.15))',
          }}
        />
      )}

      <Sidebar
        open={sidebarOpen}
        pinned={pinned}
        onTogglePin={handleTogglePin}
        onMouseLeave={() => { if (!pinned) setHoverOpen(false); }}
      />

      <TopBar sidebarPinned={pinned} />

      <main
        className="relative min-h-screen transition-all duration-300 ease-in-out"
        style={{ marginLeft: pinned ? 240 : 0, marginTop: 52, zIndex: 1 }}
      >
        <div className="max-w-7xl mx-auto p-6 animate-fade-in">
          {isImpersonating && <ImpersonationBanner />}
          <Sentry.ErrorBoundary key={location.pathname} fallback={PageErrorFallback}>
            <Outlet />
          </Sentry.ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
